import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../../components/FeatherIcon'
import { Spinner } from '../../../components/Spinner'
import { Sentinel } from '../../../library/Sentinel'
import { useMediaSrc } from '../../../library/useMediaSrc'
import { relativeTime } from '../../time'
import { ErrorLine } from '../../settings/kit'
import { errorText, type StreamingCtx } from './context'
import { ObsCard } from './ObsCard'
import { StatsModal } from './StatsModal'
import { DASHBOARD_REFERER, GO_LIVE_URL, parsePage, parsePastStream, pastStreamsQuery, PAST_LIMIT, type PastStream } from './streamingData'

/**
 * The dashboard (OnlyFans' DashboardPage): STREAMING with SCHEDULE, then the
 * past live streams that have a recording, 8 at a time, each with VIEW STATS.
 */
export function Dashboard({ ctx }: { ctx: StreamingCtx }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const { of, me } = ctx
  const [items, setItems] = useState<PastStream[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [stats, setStats] = useState<PastStream | null>(null)
  const offset = useRef(0)
  const busy = useRef(false)
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  // fetchPastStreams: the offset moves by 8 while OnlyFans says hasMore.
  const loadMore = useCallback(async () => {
    if (busy.current || !me.id) return
    busy.current = true
    setLoading(true)
    setError('')
    try {
      const page = parsePage(await of.get(`users/${me.id}/streams`, pastStreamsQuery(offset.current), DASHBOARD_REFERER), parsePastStream)
      if (!live.current) return
      if (page.hasMore) offset.current += PAST_LIMIT
      setItems((prev) => [...prev, ...page.list.filter((s) => !prev.some((p) => p.id === s.id))])
      setHasMore(page.hasMore)
    } catch (e) {
      if (live.current) setError(errorText(e, t('error')))
    } finally {
      busy.current = false
      if (live.current) setLoading(false)
    }
  }, [of, me.id, t])

  useEffect(() => {
    void loadMore()
  }, [loadMore])

  const canSchedule = me.canUploadMedia && me.isScheduledStreamsAllowed

  return (
    <div className="oset-section ofst-dash">
      <header className="oset-head">
        <button type="button" className="oset-head-btn" aria-label={t('back')} onClick={() => ctx.openPage('more')}>
          <FeatherIcon name="arrow-left" size={24} />
        </button>
        <h1 className="oset-title">{t('streaming')}</h1>
        {me.canUploadMedia && (
          <div className="oset-head-actions">
            <button type="button" className="oset-btn oset-btn--primary oset-btn--sm ofst-head-btn" disabled={!ctx.canWrite || !canSchedule} onClick={() => ctx.go('view=create')}>
              {t('schedule')}
            </button>
          </div>
        )}
      </header>
      <div className="oset-body ofst-body">
        <GoLive />
        {me.canStreaming && <ObsCard ctx={ctx} />}
        <h2 className="ofst-filter">{t('pastLiveStreams')}</h2>
        {items.length > 0 && (
          <div className="ofst-thumbs">
            {items.map((s) => (
              <PastStreamCard key={s.id} stream={s} onStats={() => setStats(s)} />
            ))}
          </div>
        )}
        {error && <ErrorLine onRetry={() => void loadMore()}>{t('loadFailed', { error })}</ErrorLine>}
        {loading && (
          <div className={items.length ? 'ofst-more' : 'ofst-center'}>
            <Spinner size={28} />
          </div>
        )}
        {!loading && !error && hasMore && items.length > 0 && <Sentinel onVisible={() => void loadMore()} token={items.length} />}
      </div>
      {stats && <StatsModal ctx={ctx} stream={stats} onClose={() => setStats(null)} />}
    </div>
  )
}

/**
 * Going live needs onlyfans.com's own page (camera, WebRTC/RTMP session): its
 * GO LIVE control, disabled here, with the way to do it there.
 */
export function GoLive() {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  return (
    <div className="ofst-golive">
      <button type="button" className="oset-btn oset-btn--outline oset-btn--sm ofst-golive-btn" disabled>
        <FeatherIcon name="radio" size={16} />
        <span>{t('goLive')}</span>
      </button>
      <p className="ofst-golive-note">
        {t('goLiveNote')}{' '}
        <a href={GO_LIVE_URL} target="_blank" rel="noopener noreferrer">
          {t('openOnOf')}
        </a>
      </p>
    </div>
  )
}

function PastStreamCard({ stream, onStats }: { stream: PastStream; onStats: () => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const { t: tp } = useTranslation('ofPages')
  const { ref, src } = useMediaSrc(stream.thumb)
  return (
    <div className="ofst-thumb">
      <div className="ofst-thumb-preview" ref={ref as React.RefObject<HTMLDivElement>}>
        {src ? <img src={src} alt="" /> : <span className="ofst-thumb-ph" aria-hidden="true" />}
        {!stream.ready && (
          <span className="ofst-thumb-processing" aria-label={t('processing')}>
            <Spinner size={22} />
          </span>
        )}
        <div className="ofst-thumb-btn">
          <button type="button" className="oset-btn oset-btn--primary oset-btn--sm" onClick={onStats}>
            {t('viewStats')}
          </button>
        </div>
      </div>
      <div className="ofst-thumb-content">
        <div>
          {t('viewers')}: <span className="ofst-viewers">{stream.viewsCount}</span>
        </div>
        <div className="ofst-thumb-time">{stream.startedAt ? relativeTime(stream.startedAt, tp) : ''}</div>
      </div>
    </div>
  )
}
